import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type TestCase } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter } from './firestoreUtils.js';
import type { TestCaseRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const TEST_CASES = 'testCases';

const converter = buildConverter<TestCase>(
  (data) => data as TestCase,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreTestCaseRepository implements TestCaseRepository {
  async findAll(): Promise<TestCase[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(collection(db, TEST_CASES), where('environment', '==', getCurrentEnvironment())).withConverter(
      converter
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<TestCase | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, TEST_CASES, id).withConverter(converter));
    return snap.data();
  }

  async findBySuiteId(suiteId: string): Promise<TestCase[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, TEST_CASES),
      where('suiteId', '==', suiteId),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findByProjectId(projectId: string): Promise<TestCase[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, TEST_CASES),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(testCase: TestCase): Promise<TestCase> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const existing = await this.findById(testCase.id);
    const data = { ...testCase, environment: getCurrentEnvironment() };
    if (existing) {
      await updateDoc(doc(db, TEST_CASES, testCase.id), { ...data, updatedAt: serverTimestamp() });
    } else {
      await setDoc(doc(db, TEST_CASES, testCase.id).withConverter(converter), data);
    }
    return { ...data, createdAt: existing?.createdAt ?? new Date().toISOString(), updatedAt: new Date().toISOString() };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, TEST_CASES, id));
  }

  async duplicate(id: string, newName?: string): Promise<TestCase | undefined> {
    const original = await this.findById(id);
    if (!original) return undefined;
    const duplicated: TestCase = {
      ...original,
      id: `tc-${Date.now()}`,
      name: newName || `${original.name} (Copy)`,
      executionOrder: original.executionOrder + 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await this.save(duplicated);
    return duplicated;
  }
}

export const createFirestoreTestCaseRepository = (): FirestoreTestCaseRepository => new FirestoreTestCaseRepository();
